import { useMemo } from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  useGA,
  useUpdaterChainId,
  useLimitOrderRouter,
  useIsValidWETHNetwork,
} from '~/hooks'
import { StyledPipe, StyledTab, StyledTabs } from '../Tabs'
import { ReactComponent as IconTool } from '~/assets/images/tool.svg'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useToken } from '~/hooks/useToken'
import { useLimitSwapPair, useLimitSwapTokens } from '~/state/dex/hooks'
import { useWeb3React } from '@web3-react/core'
import { useETHBalance, useTokenBalance } from '~/state/wallet/hooks'
import Tooltip from '~/components/Tooltip'
import { WETH as WETHAddress } from '~/constants/addresses'
import { motion } from 'framer-motion'

export const ExchangeTab: React.FC<{
  controls?: React.ReactNode
  tipVisible?: boolean
}> = ({ controls, tipVisible }) => {
  const { t } = useTranslation()
  const isLimitOrder = useLimitOrderRouter()
  const toggleTool = useModalToggle(ApplicationModal.TOOL)
  const { trackElClickCurring, trackElClick } = useGA()

  const { account } = useWeb3React()
  const chainId = useUpdaterChainId()
  const [inputToken] = useLimitSwapPair()
  const limitSwapTokens = useLimitSwapTokens(chainId)
  const queryAddress = WETHAddress[chainId]
  const WETHOnChain = useToken(queryAddress)
  const WETH = useMemo(() => {
    return WETHOnChain ? WETHOnChain : limitSwapTokens[queryAddress]
  }, [WETHOnChain, limitSwapTokens])

  const ethBalance = useETHBalance(true)
  const wethBalance = useTokenBalance(account, WETH)

  const loTipVisible = useMemo(() => {
    const visible =
      tipVisible &&
      ethBalance &&
      wethBalance &&
      inputToken?.address.toLocaleLowerCase() ===
        WETH?.address.toLocaleLowerCase()
    return visible
  }, [tipVisible, ethBalance, wethBalance, inputToken?.address])

  const showToolBtn = useIsValidWETHNetwork()

  return (
    <StyledTabs>
      <StyledTab active={!isLimitOrder} data-trackid="webtl_menu_instant">
        <Link href="/instant">{t('exchange_tab_instant')}</Link>
      </StyledTab>
      <StyledPipe />
      <StyledTab
        active={isLimitOrder}
        data-trackid="webtl_menu_lo"
        onClick={trackElClick}
      >
        <Link href="/limit-order">{t('exchange_tab_limit')}</Link>
      </StyledTab>
      <StyledControls>
        {showToolBtn && (
          <Tooltip
            overlayClassName="spot-swap-settings-tooltip lo-wrap-eth"
            arrowPointAtCenter={true}
            placement="bottom"
            title={t('lo_wrap_eth_tip')}
            trigger="click"
            visible={loTipVisible}
          >
            <motion.div
              initial={{ scale: loTipVisible ? 0 : 1 }}
              animate={{ rotate: loTipVisible ? 360 : 0, scale: 1 }}
              transition={{
                type: 'spring',
              }}
            >
              <StyledIconTool
                data-trackid="webtl_instant_tool"
                onClick={(e) => trackElClickCurring(e)(toggleTool)}
              />
            </motion.div>
          </Tooltip>
        )}
        {controls}
      </StyledControls>
    </StyledTabs>
  )
}

const StyledControls = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  .lon-tooltip {
    height: 32px;
  }
`
const StyledIconTool = styled(IconTool)`
  width: 32px;
  height: 32px;
  cursor: pointer;
`
